import React, { useState, useEffect, useCallback } from 'react';
import '../styles/InvitationsPage.css'; // Reusing modal styles

const ContractAssignmentModal = ({ 
  open, 
  onClose, 
  onSave, 
  selectedUser, 
  initialSelectedContracts = [] 
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContractIds, setSelectedContractIds] = useState(new Set(initialSelectedContracts));

  // Fetch all contracts from the API
  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      // Use the same endpoint that gets all active contracts
      const response = await fetch(`${apiBaseUrl}/api/contracts`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch contracts: ${response.status}`);
      }
      
      const data = await response.json();
      setContracts(data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setError('Failed to load contracts. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Load contracts when modal opens
  useEffect(() => {
    if (open) {
      fetchContracts();
      setSelectedContractIds(new Set(initialSelectedContracts));
    }
  }, [open, initialSelectedContracts, fetchContracts]);

  // Filter contracts based on search term
  const filteredContracts = contracts.filter(contract => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const contractName = contract.contractName || '';
    const contractNumber = contract.contractNumber || '';
    const businessUnit = contract.businessUnit || '';
    
    return contractName.toLowerCase().includes(searchLower) || 
           contractNumber.toLowerCase().includes(searchLower) ||
           businessUnit.toLowerCase().includes(searchLower);
  }).sort((a, b) => {
    // Sort alphabetically by contract name
    const nameA = (a.contractName || '').toLowerCase();
    const nameB = (b.contractName || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle contract selection (toggle)
  const handleContractToggle = (contractId) => {
    const newSelected = new Set(selectedContractIds);
    if (newSelected.has(contractId)) {
      newSelected.delete(contractId);
    } else {
      newSelected.add(contractId);
    }
    setSelectedContractIds(newSelected);
  };

  // Handle select all / deselect all
  const handleSelectAll = () => {
    if (selectedContractIds.size === filteredContracts.length) {
      // Deselect all filtered contracts
      const newSelected = new Set(selectedContractIds);
      filteredContracts.forEach(contract => {
        newSelected.delete(contract.contractId);
      });
      setSelectedContractIds(newSelected);
    } else {
      // Select all filtered contracts
      const newSelected = new Set(selectedContractIds);
      filteredContracts.forEach(contract => {
        newSelected.add(contract.contractId);
      });
      setSelectedContractIds(newSelected);
    }
  };

  // Handle save
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(Array.from(selectedContractIds));
    } catch (error) {
      setError('Failed to save contract assignments.');
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setSelectedContractIds(new Set(initialSelectedContracts));
    setSearchTerm('');
    onClose();
  };

  if (!open) return null;

  const isAllFilteredSelected = filteredContracts.length > 0 && 
    filteredContracts.every(contract => selectedContractIds.has(contract.contractId));

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ width: '85%', maxWidth: '900px', maxHeight: '80vh' }}>
        <h2 className="modal-header">Manage Contract Access</h2>
        <p>
          Select which contracts <strong>{selectedUser?.email}</strong> can access. 
          Use the checkboxes to select multiple contracts. You can search to find specific contracts.
        </p>
        
        {/* Search input */}
        <div className="form-group">
          <input
            type="text"
            placeholder="Search by contract name, number, or business unit..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="form-control"
            style={{ marginBottom: '15px' }}
          />
        </div>

        {/* Select All / Deselect All */}
        {filteredContracts.length > 0 && (
          <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              className="action-button secondary-button"
              onClick={handleSelectAll}
              style={{ fontSize: '12px', padding: '4px 8px' }}
            >
              {isAllFilteredSelected ? 'Deselect All' : 'Select All'} 
              ({filteredContracts.length})
            </button>
            <span style={{ fontSize: '14px', color: '#666' }}>
              {selectedContractIds.size} contract{selectedContractIds.size !== 1 ? 's' : ''} selected
            </span>
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="error-message" style={{ color: 'red', marginBottom: '20px', padding: '10px', backgroundColor: '#ffe6e6', borderRadius: '4px' }}>
            {error}
          </div>
        )}

        {/* Loading Indicator */}
        {loading ? (
          <div className="loading-indicator" style={{ textAlign: 'center', padding: '40px 0' }}>
            Loading contracts...
          </div>
        ) : (
          /* Contracts List with proper scrolling */
          <div style={{ 
            overflowY: 'auto', 
            maxHeight: '350px', 
            border: '1px solid #ddd', 
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            {contracts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                No contracts available for assignment.
              </div>
            ) : filteredContracts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                No contracts found matching "{searchTerm}". Try a different search term.
              </div>
            ) : (
              <table className="invitations-table" style={{ margin: 0, boxShadow: 'none' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input 
                        type="checkbox" 
                        checked={isAllFilteredSelected}
                        onChange={handleSelectAll}
                        title={isAllFilteredSelected ? 'Deselect all' : 'Select all'}
                      />
                    </th>
                    <th>Contract Name</th>
                    <th>Contract Number</th>
                    <th>Business Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContracts.map((contract) => {
                    const isSelected = selectedContractIds.has(contract.contractId);
                    return (
                      <tr 
                        key={contract.contractId} 
                        onClick={() => handleContractToggle(contract.contractId)}
                        className={isSelected ? 'selected-row' : ''}
                        style={{ 
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#f0e6ff' : 'inherit'
                        }}
                      >
                        <td>
                          <input 
                            type="checkbox" 
                            checked={isSelected}
                            onChange={() => handleContractToggle(contract.contractId)}
                          />
                        </td>
                        <td style={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                          {contract.contractName || 'N/A'}
                        </td>
                        <td>{contract.contractNumber || 'N/A'}</td>
                        <td>{contract.businessUnit || 'N/A'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
        
        {/* Modal Actions */}
        <div className="form-actions" style={{ marginTop: '20px' }}>
          <button 
            type="button" 
            className="action-button" 
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="action-button primary-button"
            disabled={loading || saving}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : `Save Access (${selectedContractIds.size} contracts)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractAssignmentModal; 